import type { AssignmentTypeValue, DropZoneItemResponse } from "../../Api/schedules";
import type { BracketSchedulingConfig } from "../../Common/Brackets/types";
import { getBracketSlicingStrategy } from "./bracketSlicingStrategies";
import { BracketType, BracketTypeEnum } from "../../Common/types";

export const RoundMarkerEnum = {
    OneOf128: "1/128",
    OneOf64: "1/64",
    OneOf32: "1/32",
    OneOf16: "1/16",
    OneOf8: "1/8",
    QuarterFinal: "quarter_final",
    SemiFinal: "semi_final",
    Final: "final",
    ThirdPlace: "third_place",
} as const;

export type RoundMarker = (typeof RoundMarkerEnum)[keyof typeof RoundMarkerEnum];

export function parseRoundMarker(value: string | null): RoundMarker | null {
    if (value === null) {
        return null;
    }
    if (!(Object.values(RoundMarkerEnum) as string[]).includes(value)) {
        throw new Error(`Unknown RoundMarker: "${value}"`);
    }
    return value as RoundMarker;
}

export function mapDropZoneItemResponse(response: DropZoneItemResponse): DropZoneItem {
    return {
        ...response,
        roundMarkerFrom: parseRoundMarker(response.roundMarkerFrom),
        roundMarkerTo: parseRoundMarker(response.roundMarkerTo),
        segment: {
            ...response.segment,
            from: parseRoundMarker(response.segment.from),
            to: parseRoundMarker(response.segment.to),
        },
        bracketType: response.bracketType as BracketType,
    };
}

export type BracketSegment = {
    from: RoundMarker | null;
    to: RoundMarker | null;
    equalParts: 1 | 2 | 4;
    isRest: boolean;
    includesThirdPlace: boolean;
};

export type DropZoneItem = {
    key: string;
    parameterId: number;
    name: string;
    totalRealCompetitors: number;
    assignmentType: AssignmentTypeValue;
    roundMarkerFrom: RoundMarker | null;
    roundMarkerTo: RoundMarker | null;
    segmentIndex: number;
    partIndex: number;
    partsCount: 1 | 2 | 4;
    segment: BracketSegment;
    bracketType: BracketType;
    hasSchedulingConfig: boolean;
    schedulingConfig: BracketSchedulingConfig | null;
    supportsRoundSlicing: boolean;
};

export type BulkSaveGroup = {
    assignmentType: DropZoneItem["assignmentType"];
    roundMarkerFrom: DropZoneItem["roundMarkerFrom"];
    roundMarkerTo: DropZoneItem["roundMarkerTo"];
    items: DropZoneItem[];
};

export function groupSlotKey(item: Pick<DropZoneItem, "assignmentType" | "roundMarkerFrom" | "roundMarkerTo">): string {
    return `${item.assignmentType}_${item.roundMarkerFrom ?? ""}_${item.roundMarkerTo ?? ""}`;
}

export function canConfigureBracketSlicing(item: Pick<DropZoneItem, "bracketType" | "hasSchedulingConfig">): boolean {
    return item.bracketType !== BracketTypeEnum.RoundRobin && !item.hasSchedulingConfig;
}

function buildDropZoneItemPreviewKey(parameterId: number, segmentIndex: number, partIndex: number): string {
    // Mirrors \Panel\Schedule\Model\DropZoneItem::key().
    // API-loaded DropZone items use response.key; this is only for SliceToolModal's unsaved previews.
    return `${parameterId}_${segmentIndex}_${partIndex}`;
}

export type BracketSlicingTarget = {
    parameter_id: number;
    name: string;
    total_real_competitors: number;
    bracketType: BracketType;
    schedulingConfig: BracketSchedulingConfig | null;
    supportsRoundSlicing: boolean;
};

// TODO: These numeric values mirror backend `ScheduledCategory::TYPE_*` constants. There is no enforced
// contract — if the backend enum changes, this must be updated manually. Consider generating from a shared
// type definition or adding a test that verifies the mapping matches the API response.
const PART_ASSIGNMENT_TYPES: Record<1 | 2 | 4, AssignmentTypeValue[]> = {
    1: [1],
    2: [2, 4],
    4: [8, 16, 32, 64],
};

export const WHOLE_BRACKET_EQUAL_PARTS_KEY = "whole_bracket";

/**
 * Expands a single bracket into DropZone items based on its schedulingConfig.
 * Used by SliceToolModal for live preview of config changes.
 */
export function expandBracketToDropZoneItems(bracket: BracketSlicingTarget): DropZoneItem[] {
    if (bracket.schedulingConfig === null) {
        return [
            createDropZoneItem(
                bracket,
                {
                    from: null,
                    to: null,
                    equalParts: 1,
                    isRest: true,
                    includesThirdPlace: false,
                },
                0,
                0,
            ),
        ];
    }

    return resolveSegments(bracket.schedulingConfig, bracket.total_real_competitors, bracket.bracketType).flatMap(
        (segment, segmentIndex) => {
            const assignmentTypes = PART_ASSIGNMENT_TYPES[segment.equalParts];

            return assignmentTypes.map((assignmentType, partIndex) =>
                createDropZoneItem(bracket, segment, segmentIndex, partIndex, assignmentType),
            );
        },
    );
}

export function resolveSegments(config: BracketSchedulingConfig, totalRealCompetitors: number, bracketType: BracketType): BracketSegment[] {
    const strategy = getBracketSlicingStrategy(bracketType);
    const isolatedSet = new Set(config.isolatedRounds);
    const mainRounds = strategy.getAvailableRounds(totalRealCompetitors).filter(round => round !== "third_place");
    const segments: BracketSegment[] = [];
    let restAccumulator: RoundMarker[] = [];

    mainRounds.forEach(round => {
        if (isolatedSet.has(round)) {
            if (restAccumulator.length > 0) {
                segments.push(makeRestSegment(restAccumulator));
                restAccumulator = [];
            }
            segments.push({
                from: round,
                to: round,
                equalParts: equalPartsFor(config, round),
                isRest: false,
                includesThirdPlace: false,
            });
            return;
        }

        restAccumulator.push(round);
    });

    if (restAccumulator.length > 0) {
        segments.push(makeRestSegment(restAccumulator));
    }

    let resolvedSegments =
        segments.length === 0
            ? [
                  {
                      from: null,
                      to: null,
                      equalParts: wholeBracketEqualPartsFor(config),
                      isRest: true,
                      includesThirdPlace: false,
                  } as BracketSegment,
              ]
            : segments;

    if (resolvedSegments.length === 1 && config.isolatedRounds.length === 0) {
        resolvedSegments = [
            {
                ...resolvedSegments[0],
                equalParts: wholeBracketEqualPartsFor(config),
            },
        ];
    }

    resolvedSegments = strategy.finalizeSegments(resolvedSegments, config);

    return applyNullBoundaries(resolvedSegments);
}

export function getAvailableRounds(totalRealCompetitors: number, bracketType: BracketType): RoundMarker[] {
    return getBracketSlicingStrategy(bracketType).getAvailableRounds(totalRealCompetitors);
}

export function assignmentTypeForPart(equalParts: 1 | 2 | 4, partIndex: number): AssignmentTypeValue {
    return PART_ASSIGNMENT_TYPES[equalParts][partIndex] ?? 1;
}

export function getRoundMarkerTranslationKey(marker: RoundMarker): string {
    return `admin_schedule_manage.round_marker.${marker}`;
}

export function getRoundMarkerTagClass(from: RoundMarker | null, to: RoundMarker | null): string {
    if (from === null && to === null) {
        return "";
    }
    if (to === null || to === "final") {
        return "round-coverage-tag-final";
    }
    if (to === "third_place") {
        return "round-coverage-tag-third-place";
    }
    if (to === "semi_final") {
        return "round-coverage-tag-semi-final";
    }
    if (to === "quarter_final") {
        return "round-coverage-tag-quarter-final";
    }
    return "round-coverage-tag-early"; // early rounds: 1/8, 1/16, 1/32, 1/64, 1/128
}

export function getAvailableEqualPartsForRound(round: RoundMarker): readonly (1 | 2 | 4)[] {
    if (round === "final" || round === "third_place") {
        return [1];
    }
    if (round === "semi_final") {
        return [1, 2];
    }
    return [1, 2, 4];
}

/**
 * Mirrors `BracketSchedulingConfig::availableWholeBracketEqualParts` (PHP) exactly.
 */
export function getAvailableEqualPartsForWholeBracket(totalRealCompetitors: number): readonly (1 | 2 | 4)[] {
    const bracketSize = nextPowerOfTwo(totalRealCompetitors);

    return ([1, 2, 4] as const).filter(parts => bracketSize >= parts * 4);
}

function nextPowerOfTwo(n: number): number {
    if (n <= 1) {
        return 1;
    }

    let power = 1;
    while (power < n) {
        power *= 2;
    }

    return power;
}

function createDropZoneItem(
    bracket: BracketSlicingTarget,
    segment: BracketSegment,
    segmentIndex: number,
    partIndex: number,
    assignmentType: AssignmentTypeValue = assignmentTypeForPart(segment.equalParts, partIndex),
): DropZoneItem {
    return {
        key: buildDropZoneItemPreviewKey(bracket.parameter_id, segmentIndex, partIndex),
        parameterId: bracket.parameter_id,
        name: bracket.name,
        totalRealCompetitors: bracket.total_real_competitors,
        assignmentType,
        roundMarkerFrom: segment.from,
        roundMarkerTo: segment.to,
        segmentIndex,
        partIndex,
        partsCount: segment.equalParts,
        segment,
        bracketType: bracket.bracketType,
        hasSchedulingConfig: bracket.schedulingConfig !== null,
        schedulingConfig: bracket.schedulingConfig,
        supportsRoundSlicing: bracket.supportsRoundSlicing,
    };
}

export function equalPartsFor(config: BracketSchedulingConfig, round: RoundMarker): 1 | 2 | 4 {
    const configured = config.equalParts[round];

    if (configured === 2 || configured === 4) {
        return configured;
    }

    return 1;
}

function wholeBracketEqualPartsFor(config: BracketSchedulingConfig): 1 | 2 | 4 {
    const configured = config.equalParts[WHOLE_BRACKET_EQUAL_PARTS_KEY];

    if (configured === 2 || configured === 4) {
        return configured;
    }

    return 1;
}

function makeRestSegment(rounds: RoundMarker[]): BracketSegment {
    return {
        from: rounds[0],
        to: rounds[rounds.length - 1],
        equalParts: 1,
        isRest: true,
        includesThirdPlace: false,
    };
}

function applyNullBoundaries(segments: BracketSegment[]): BracketSegment[] {
    if (segments.length === 0) {
        return segments;
    }

    return segments.map((segment, index) => {
        if (segments.length === 1 && segment.isRest) {
            return { ...segment, from: null, to: null };
        }

        if (index === 0) {
            return { ...segment, from: null };
        }

        if (index === segments.length - 1 && segment.isRest) {
            return { ...segment, to: null };
        }

        return segment;
    });
}
